import { ENTITY_TYPES, DEFAULT_ENTITY_TYPE } from '../chunk-VBCSY26D.js';
export { DEFAULT_ENTITY_TYPE, ENTITY_TYPES, ENTITY_TYPE_LABEL, entityTypeLabel, entityTypeOptions, isEntityType } from '../chunk-VBCSY26D.js';
export { EMPTY_PRIMARY_STATE, PRIMARY_KINDS, PRIMARY_RUNG_OWNER, applyPrimaryFlags, customisationReach, deriveCatalogOwnership, isReadOnlyCatalogRow, presentationOverrideOf, primaryAppliesTo, primaryChoiceAt, primaryChoiceFromWire, primaryHiddenBy, primarySetBy, primaryStateFromWire, resolvePrimaryChoices, samePrimaryChain } from '../chunk-XVYJS5GC.js';
import { isHexColor } from '../chunk-TZJXF4IU.js';
import { FieldOverridesSchema } from '../chunk-B6HT6G46.js';
import '../chunk-6XJKVJAV.js';
import { Tier, mergeByPrecedence } from '../chunk-MY2WTNZQ.js';
import '../chunk-ZLHKQ343.js';
import '../chunk-AVMRSLPT.js';
export { GLOBAL_TENANT } from '../chunk-BJ3WKLB6.js';
import '../chunk-INX63VAT.js';
import '../chunk-J3WEQQIH.js';
import '../chunk-JURWUKW3.js';
import '../chunk-IQZNGZOU.js';
import '../chunk-BFUJOKOR.js';
import '../chunk-UEXNJL5R.js';
import '../chunk-FOKBTIWA.js';
import '../chunk-3WVQTE7S.js';
import '../chunk-O2TUCS6S.js';
import { asString } from '../chunk-ZDF5AAM3.js';
import '../chunk-YZM7XXAM.js';
import { toStoredChainId, toChainSlug } from '../chunk-DXLSREFI.js';
import '../chunk-GPJ572YS.js';
import '../chunk-N7SZ66QO.js';
export { catalogIdsByKind, catalogKindOfId, defaultTagId } from '../chunk-AHISJLEK.js';
import '../chunk-QXYP3KHC.js';
import '../chunk-PZ5AY32C.js';
import { z } from 'zod';

var CatalogItemKind = {
  TAG: "tag",
  LABEL: "label",
  ENTITY: "entity",
  GROUP: "group",
  ADDRESS: "address"
};
var CATALOG_ITEM_KINDS = Object.values(CatalogItemKind);
var CATALOG_ITEM_KIND_LABEL = {
  tag: "Tag",
  label: "Label",
  entity: "Entity",
  group: "Group",
  address: "Address"
};
var CATALOG_LIFECYCLES = ["verified", "candidate", "deprecated", "quarantined"];
var CATALOG_LIFECYCLE_LABEL = {
  verified: "Verified",
  candidate: "Candidate",
  deprecated: "Deprecated",
  quarantined: "Quarantined"
};
var lifecycleSchema = z.enum(CATALOG_LIFECYCLES);
var severitySchema = z.enum(["info", "low", "medium", "high", "critical"]);
var riskLevelSchema = z.enum(["safe", "low", "medium", "high", "severe"]);
var hexColor = z.string().trim().refine((v) => v === "" || isHexColor(v), { message: "Enter a valid hex colour (e.g. #3b82f6)" });
var baseShape = {
  name: z.string().trim().min(1, "Name is required").max(120, "Name is too long"),
  description: z.string().trim().max(2e3).optional(),
  /** Hex colour for chips/avatars. */
  color: hexColor.optional(),
  /** Logo URI / S3 key (set by the upload flow). */
  logo: z.string().trim().optional(),
  aliases: z.array(z.string().trim().min(1)).optional(),
  /**
   * Chains this row applies to (hex chain id or slug). Empty / omitted = all
   * chains (chain-agnostic). Persisted as `CatalogAssignment` rows by the
   * `@authe/catalog` data layer.
   */
  chains: z.array(z.string().trim().min(1)).optional(),
  /**
   * Chains this row is *primarily / canonically* used on (hex chain id or
   * slug). A presentation hint persisted on the row itself — distinct from
   * `chains` (applicability join). Empty / omitted = none flagged.
   */
  primaryChains: z.array(z.string().trim().min(1)).optional(),
  lifecycle: lifecycleSchema.optional(),
  severity: severitySchema.optional(),
  riskLevel: riskLevelSchema.optional()
};
var tagSchema = z.object({
  ...baseShape,
  kind: z.literal("tag"),
  category: z.string().trim().max(80).optional()
});
var labelSchema = z.object({
  ...baseShape,
  kind: z.literal("label"),
  /** Address-bound label (optional — a label can be global). */
  address: z.string().trim().optional(),
  /** Chain for the bound address. */
  chain: z.string().trim().optional()
});
var entitySchema = z.object({
  ...baseShape,
  kind: z.literal("entity"),
  // The backend's `EntityType` enum, not free text — see `./entityTypes`.
  entityType: z.enum(ENTITY_TYPES, { message: "Pick one of the listed types" }).optional(),
  website: z.string().trim().url("Enter a valid URL").optional().or(z.literal("")),
  twitter: z.string().trim().max(80).optional()
});
var groupSchema = z.object({
  ...baseShape,
  kind: z.literal("group"),
  memberTagIds: z.array(z.string()).optional(),
  memberLabelIds: z.array(z.string()).optional(),
  memberEntityIds: z.array(z.string()).optional()
});
var addressSchema = z.object({
  ...baseShape,
  kind: z.literal("address"),
  address: z.string().trim().min(1, "Address is required"),
  chain: z.string().trim().min(1, "Chain is required"),
  primaryEntityId: z.string().trim().optional()
});
var CatalogItemDraftSchema = z.discriminatedUnion("kind", [
  tagSchema,
  labelSchema,
  entitySchema,
  groupSchema,
  addressSchema
]);
function emptyDraft(kind) {
  const base = { name: "" };
  switch (kind) {
    case "tag":
      return { kind, ...base };
    case "label":
      return { kind, ...base };
    case "entity":
      return { kind, ...base, entityType: DEFAULT_ENTITY_TYPE };
    case "group":
      return { kind, ...base };
    case "address":
      return { kind, ...base, address: "", chain: "" };
  }
}
function validateDraft(draft) {
  const result = CatalogItemDraftSchema.safeParse(draft);
  if (!result.success) {
    const flat = result.error.flatten();
    return {
      success: false,
      fieldErrors: flat.fieldErrors,
      message: flat.formErrors[0]
    };
  }
  const stray = primaryChainsOutsideAppliesTo(result.data);
  if (stray.length > 0) {
    const noun = stray.length === 1 ? "chain is" : "chains are";
    return {
      success: false,
      fieldErrors: {
        primaryChains: [
          `Primary ${noun} not in \u201CApplies to chains\u201D: ${stray.join(", ")}. Add them to \u201CApplies to chains\u201D or remove them from \u201CPrimary chains\u201D.`
        ]
      },
      message: "Primary chains must be within \u201CApplies to chains\u201D."
    };
  }
  return { success: true, data: result.data, fieldErrors: {} };
}
function primaryChainsOutsideAppliesTo(draft) {
  const applies = draft.chains ?? [];
  if (applies.length === 0) return [];
  const inScope = new Set(applies);
  return (draft.primaryChains ?? []).filter((c) => !inScope.has(c));
}
function isChainAgnostic(draft) {
  return !draft.chains || draft.chains.length === 0;
}

// src/catalog/mappers.ts
var undef = (v) => v == null || v === "" ? void 0 : v;
var storeChain = (v) => {
  const t = undef(v);
  return t ? toStoredChainId(t) ?? t : void 0;
};
function draftToRowBody(draft) {
  const common = {
    name: draft.name.trim(),
    description: undef(draft.description),
    // Lifecycle can inherit the platform default; an assessment cannot.
    lifecycle: draft.lifecycle,
    // Null explicitly clears an assessment on edit. Omission means no patch.
    severity: draft.kind === "address" ? draft.severity : draft.severity ?? null,
    aliases: draft.aliases && draft.aliases.length > 0 ? draft.aliases : void 0,
    // Send the WHOLE desired primary-chain list whenever it's defined — including
    // `[]` to explicitly clear it — so the backend can reconcile the single-winner
    // primary pointers (set/evict/retire) off an authoritative set. Only omit when
    // `undefined` (not loaded / not part of this edit) to avoid clobbering.
    primary_chains: draft.primaryChains !== void 0 ? draft.primaryChains.map((c) => storeChain(c) ?? c) : void 0
  };
  switch (draft.kind) {
    case "tag":
      return { ...common, color: undef(draft.color), icon: undef(draft.logo), category: undef(draft.category) };
    case "label":
      return {
        ...common,
        color: undef(draft.color),
        icon: undef(draft.logo),
        risk_level: draft.riskLevel ?? null,
        address: undef(draft.address),
        chain: storeChain(draft.chain)
      };
    case "group":
      return {
        ...common,
        color: undef(draft.color),
        icon: undef(draft.logo),
        member_tag_ids: draft.memberTagIds,
        member_label_ids: draft.memberLabelIds,
        member_entity_ids: draft.memberEntityIds
      };
    case "entity":
      return {
        ...common,
        color: undef(draft.color),
        logo_s3_uri: undef(draft.logo),
        risk_level: draft.riskLevel ?? null,
        entity_type: undef(draft.entityType),
        website: undef(draft.website),
        twitter: undef(draft.twitter)
      };
    case "address":
      return {
        ...common,
        logo_s3_uri: undef(draft.logo),
        risk_level: draft.riskLevel,
        address: draft.address.trim(),
        chain: toStoredChainId(draft.chain.trim()) ?? draft.chain.trim(),
        primary_entity_id: undef(draft.primaryEntityId)
      };
  }
}
var strArr = (v) => Array.isArray(v) && v.length > 0 ? v.filter((x) => typeof x === "string") : void 0;
function rowToDraft(kind, row) {
  const base = {
    name: asString(row.name) ?? "",
    description: asString(row.description),
    aliases: strArr(row.aliases),
    primaryChains: strArr(row.primary_chains)?.map((c) => toChainSlug(c) ?? c),
    lifecycle: asString(row.lifecycle),
    severity: asString(row.severity)
  };
  switch (kind) {
    case "tag":
      return { kind, ...base, color: asString(row.color), logo: asString(row.icon), category: asString(row.category) };
    case "label":
      return {
        kind,
        ...base,
        color: asString(row.color),
        logo: asString(row.icon),
        riskLevel: asString(row.risk_level),
        address: asString(row.address),
        chain: toChainSlug(asString(row.chain)) ?? void 0
      };
    case "group":
      return {
        kind,
        ...base,
        color: asString(row.color),
        logo: asString(row.icon),
        memberTagIds: strArr(row.member_tag_ids),
        memberLabelIds: strArr(row.member_label_ids),
        memberEntityIds: strArr(row.member_entity_ids)
      };
    case "entity":
      return {
        kind,
        ...base,
        color: asString(row.color),
        logo: asString(row.logo_s3_uri),
        riskLevel: asString(row.risk_level),
        // Cast, not narrowed: a stored value outside the enum must reach the
        // form as itself — `entityTypeOptions` shows it, `validateDraft` refuses it.
        entityType: asString(row.entity_type),
        website: asString(row.website),
        twitter: asString(row.twitter)
      };
    case "address":
      return {
        kind,
        ...base,
        logo: asString(row.logo_s3_uri),
        riskLevel: asString(row.risk_level),
        address: asString(row.address) ?? "",
        chain: toChainSlug(asString(row.chain)) ?? "",
        primaryEntityId: asString(row.primary_entity_id)
      };
  }
}

// src/catalog/links.ts
var WEB_SCHEMES = /* @__PURE__ */ new Set(["http:", "https:"]);
var TWITTER_HOSTS = /* @__PURE__ */ new Set([
  "twitter.com",
  "www.twitter.com",
  "x.com",
  "www.x.com",
  "mobile.twitter.com"
]);
function webUrl(value) {
  const raw = (value ?? "").trim();
  if (!raw) return void 0;
  const candidate = /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`;
  let parsed;
  try {
    parsed = new URL(candidate);
  } catch {
    return void 0;
  }
  if (!WEB_SCHEMES.has(parsed.protocol) || !parsed.hostname) return void 0;
  return parsed.toString();
}
function webUrlLabel(value) {
  const url = webUrl(value);
  if (!url) return (value ?? "").trim() || void 0;
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}
function twitterLink(value) {
  const raw = (value ?? "").trim();
  if (!raw) return void 0;
  let handle = raw;
  if (raw.includes("/") || /^[a-z][a-z0-9+.-]*:/i.test(raw)) {
    const url = webUrl(raw);
    if (!url) return void 0;
    const parsed = new URL(url);
    if (!TWITTER_HOSTS.has(parsed.hostname.toLowerCase())) return void 0;
    handle = parsed.pathname.split("/").filter(Boolean)[0] ?? "";
  }
  handle = handle.replace(/^@+/, "");
  if (!/^\w{1,15}$/.test(handle)) return void 0;
  return { handle: `@${handle}`, url: `https://x.com/${handle}` };
}

// src/catalog/fields.ts
var CATALOG_COMMON_FIELDS = {
  id: "id",
  name: "name",
  description: "description",
  aliases: "aliases",
  lifecycle: "lifecycle",
  severity: "severity",
  source: "source",
  confidence: "confidence",
  /** Glass-box "why" bullets rendered from evidence (`commons.evidence.explain`). */
  explanation: "explanation",
  isHumanApproved: "is_human_approved",
  systemDefault: "system_default",
  metadata: "metadata",
  overridesByChain: "overrides_by_chain",
  customerId: "customer_id",
  orgIdTenantId: "org_id_tenant_id",
  createdAt: "created_at",
  createdBy: "created_by",
  updatedAt: "updated_at",
  updatedBy: "updated_by",
  validFrom: "valid_from",
  validTo: "valid_to"
};
var ENTITY_FIELDS = {
  ...CATALOG_COMMON_FIELDS,
  entityType: "entity_type",
  riskLevel: "risk_level",
  website: "website",
  twitter: "twitter",
  /** Entities/addresses store the logo as an S3 URI (tags/labels/groups use `icon`). */
  logo: "logo_s3_uri",
  parentEntityId: "parent_entity_id",
  relatedEntityIds: "related_entity_ids"
};
var TAG_FIELDS = {
  ...CATALOG_COMMON_FIELDS,
  color: "color",
  icon: "icon",
  category: "category"
};
var LABEL_FIELDS = {
  ...CATALOG_COMMON_FIELDS,
  color: "color",
  icon: "icon",
  riskLevel: "risk_level",
  /** Labels can be bound to a specific address on a chain. */
  address: "address",
  chain: "chain"
};
var GROUP_FIELDS = {
  ...CATALOG_COMMON_FIELDS,
  color: "color",
  icon: "icon",
  memberTagIds: "member_tag_ids",
  memberLabelIds: "member_label_ids",
  memberEntityIds: "member_entity_ids"
};
var CATALOG_FIELDS = {
  entity: Object.values(ENTITY_FIELDS),
  tag: Object.values(TAG_FIELDS),
  label: Object.values(LABEL_FIELDS),
  group: Object.values(GROUP_FIELDS)
};
function catalogFields(kind) {
  return CATALOG_FIELDS[kind] ?? [];
}

// src/catalog/applicability.ts
var ALL_CHAINS = null;
function assignmentAppliesToChain(assignment, chainId) {
  return assignment.chain_id == null || assignment.chain_id === chainId;
}
function appliesToAllChains(assignments) {
  return assignments.some((a) => a.chain_id == null);
}
function assignedChains(assignments) {
  const set = /* @__PURE__ */ new Set();
  for (const a of assignments) if (a.chain_id != null) set.add(a.chain_id);
  return [...set];
}
function chainsFromAssignments(assignments) {
  return appliesToAllChains(assignments) ? [] : assignedChains(assignments);
}
function assignmentsForChain(assignments, chainId) {
  return assignments.filter((a) => assignmentAppliesToChain(a, chainId));
}
var hexColor2 = z.string().trim().refine((v) => v === "" || isHexColor(v), { message: "Enter a valid hex colour (e.g. #3b82f6)" });
var FieldOverridesDraftSchema = z.object({
  name: z.string().trim().max(120, "Name is too long").optional(),
  description: z.string().trim().max(2e3).optional(),
  color: hexColor2.optional(),
  icon: z.string().trim().optional(),
  logo: z.string().trim().optional(),
  aliases: z.array(z.string().trim().min(1)).optional(),
  metadata: z.record(z.string(), z.any()).optional()
});
var trimmed = (v) => {
  if (v == null) return void 0;
  const t = v.trim();
  return t === "" ? void 0 : t;
};
function overridesDraftFromDto(dto) {
  if (!dto) return {};
  return {
    name: dto.name ?? void 0,
    description: dto.description ?? void 0,
    color: dto.color ?? void 0,
    icon: dto.icon ?? void 0,
    logo: dto.logo ?? void 0,
    aliases: dto.aliases ?? void 0,
    metadata: dto.metadata ?? void 0
  };
}
function overridesDraftToDto(draft) {
  return FieldOverridesSchema.parse({
    name: trimmed(draft.name) ?? null,
    description: trimmed(draft.description) ?? null,
    color: trimmed(draft.color) ?? null,
    icon: trimmed(draft.icon) ?? null,
    logo: trimmed(draft.logo) ?? null,
    aliases: draft.aliases && draft.aliases.length > 0 ? draft.aliases : null,
    metadata: draft.metadata && Object.keys(draft.metadata).length > 0 ? draft.metadata : null
  });
}
function isEmptyFieldOverrides(draft) {
  if (!draft) return true;
  return !trimmed(draft.name) && !trimmed(draft.description) && !trimmed(draft.color) && !trimmed(draft.icon) && !trimmed(draft.logo) && (!draft.aliases || draft.aliases.length === 0) && (!draft.metadata || Object.keys(draft.metadata).length === 0);
}
function applyFieldOverrides(base, override) {
  const o = override ?? {};
  const pick = (ov, fallback) => {
    const t = trimmed(ov);
    return t ?? fallback;
  };
  const aliases = o.aliases && o.aliases.length > 0 ? o.aliases : base.aliases;
  const oMeta = o.metadata ?? void 0;
  const metadata = base.metadata || oMeta ? { ...base.metadata ?? {}, ...oMeta ?? {} } : void 0;
  return {
    name: pick(o.name, base.name),
    description: pick(o.description, base.description),
    color: pick(o.color, base.color),
    icon: pick(o.icon, base.icon),
    logo: pick(o.logo, base.logo),
    aliases,
    metadata
  };
}
function diffFieldOverrides(initial, current) {
  const chains = /* @__PURE__ */ new Set([...Object.keys(initial), ...Object.keys(current)]);
  const changes = [];
  for (const chainId of chains) {
    const cur = current[chainId];
    const hadBefore = chainId in initial && !isEmptyFieldOverrides(initial[chainId]);
    if (isEmptyFieldOverrides(cur)) {
      if (hadBefore) changes.push({ chainId, value: null });
      continue;
    }
    const nextDto = overridesDraftToDto(cur);
    const prevDto = hadBefore ? overridesDraftToDto(initial[chainId]) : null;
    if (!prevDto || JSON.stringify(prevDto) !== JSON.stringify(nextDto)) {
      changes.push({ chainId, value: nextDto });
    }
  }
  return changes;
}

// src/catalog/inheritance.ts
var defaultKey = (hit) => `${hit.kind}\0${hit.catalogId}`;
function inheritEntityHits(entityHits, entityId) {
  return entityHits.map((hit) => ({
    ...hit,
    tier: Tier.ENTITY_INHERITED,
    inheritedFromEntityId: entityId
  }));
}
function resolveInheritedHits(opts) {
  const keyFn = opts.keyFn ?? defaultKey;
  const all = [...opts.direct ?? []];
  for (const source of opts.entities ?? []) {
    all.push(...inheritEntityHits(source.hits, source.entityId));
  }
  return mergeByPrecedence(all, keyFn, (hit) => hit.tier ?? Tier.PROVIDER_CANDIDATE);
}
function isInheritedHit(hit) {
  return hit.tier === Tier.ENTITY_INHERITED || hit.inheritedFromEntityId != null;
}

// src/catalog/retirement.ts
function retirementImpact(input) {
  const noun = CATALOG_ITEM_KIND_LABEL[input.kind].toLowerCase();
  if (input.loading) {
    return {
      sentence: "Counting what is attached to it\u2026",
      tone: "counting",
      approximate: false
    };
  }
  if (input.failed || input.total === null) {
    return {
      sentence: `How much is attached to this ${noun} could not be read, so this may detach more than you expect.`,
      tone: "unknown",
      approximate: false
    };
  }
  if (input.total <= 0) {
    return {
      sentence: `Nothing is attached to it, so nothing else changes.`,
      tone: "none",
      approximate: false
    };
  }
  const approximate = input.totalCapped === true;
  const count = input.total.toLocaleString();
  const rows = input.total === 1 ? "attachment" : "attachments";
  return {
    sentence: approximate ? `At least ${count} ${rows} will be detached \u2014 addresses and transactions this ${noun} is on.` : `${count} ${rows} will be detached \u2014 addresses and transactions this ${noun} is on.`,
    tone: "some",
    approximate
  };
}

// src/catalog/resourceChanges.ts
var catalogResource = (kind) => `catalog.${kind}`;
var CATALOG_PRIMARY_RESOURCE = "catalog.primary";
var CATALOG_RESOURCES = [...CATALOG_ITEM_KINDS.map(catalogResource), CATALOG_PRIMARY_RESOURCE];

// src/catalog/duplicate.ts
var NAME_MAX = 120;
var SUFFIX = " (copy)";
function duplicateName(name) {
  const base = name.trim();
  if (!base) return "";
  const room = NAME_MAX - SUFFIX.length;
  return `${base.length > room ? base.slice(0, room).trimEnd() : base}${SUFFIX}`;
}
function duplicateDraft(kind, row) {
  const draft = rowToDraft(kind, row);
  const chains = Array.isArray(row.chains) ? row.chains.filter((c) => typeof c === "string" && c !== "").map((c) => toChainSlug(c) ?? c) : void 0;
  return {
    ...draft,
    name: duplicateName(draft.name),
    lifecycle: void 0,
    ...chains && chains.length > 0 ? { chains } : {}
  };
}

export { ALL_CHAINS, CATALOG_COMMON_FIELDS, CATALOG_FIELDS, CATALOG_ITEM_KINDS, CATALOG_ITEM_KIND_LABEL, CATALOG_LIFECYCLES, CATALOG_LIFECYCLE_LABEL, CATALOG_PRIMARY_RESOURCE, CATALOG_RESOURCES, CatalogItemDraftSchema, CatalogItemKind, ENTITY_FIELDS, FieldOverridesDraftSchema, GROUP_FIELDS, LABEL_FIELDS, TAG_FIELDS, appliesToAllChains, applyFieldOverrides, assignedChains, assignmentAppliesToChain, assignmentsForChain, catalogFields, catalogResource, chainsFromAssignments, diffFieldOverrides, draftToRowBody, duplicateDraft, duplicateName, emptyDraft, inheritEntityHits, isChainAgnostic, isEmptyFieldOverrides, isInheritedHit, overridesDraftFromDto, overridesDraftToDto, primaryChainsOutsideAppliesTo, resolveInheritedHits, retirementImpact, rowToDraft, twitterLink, validateDraft, webUrl, webUrlLabel };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map